import { Card, Box, Typography, Chip, Stack, useTheme, alpha, IconButton, Badge } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  Star as StarIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as ShoppingCartIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';
import LazyImage from '../LazyImage';
import { OnePieceCard } from '../../types/onepiece';
import { 
  useToggleCardOwnership, 
  useToggleCardWishlist, 
  useToggleCardFavorite 
} from '../../hooks/useCardOwnership';
import { 
  useToggleOnePieceCardOwnership, 
  useToggleOnePieceCardWishlist, 
  useToggleOnePieceCardFavorite 
} from '../../hooks/useOnePieceCardOwnership';

interface GameCardProps {
  card: any; 
  game: 'pokemon' | 'onepiece';
  layout?: 'grid' | 'flat';
  onClick?: (card: any) => void;
  showSetName?: boolean; 
  setName?: string; // Optional: Override set name
  ownership?: any; // Optional: Ownership data for icons
}

// Helper for OP Colors
const getOpColor = (color: string) => {
  const map: Record<string, string> = {
    Red: '#e74c3c', Blue: '#3498db', Green: '#2ecc71',
    Purple: '#9b59b6', Black: '#34495e', Yellow: '#f39c12'
  };
  return map[color] || '#95a5a6';
};

export const GameCard: React.FC<GameCardProps> = ({ 
  card, 
  game, 
  layout = 'grid', 
  onClick,
  showSetName,
  setName: overrideSetName,
  ownership
}) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { showError } = useNotification();

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      showError('Please sign in to manage your collection');
      return;
    }
    action();
  };

  // Hooks for toggling
  const togglePkOwnership = useToggleCardOwnership();
  const togglePkWishlist = useToggleCardWishlist();
  const togglePkFavorite = useToggleCardFavorite();

  const toggleOpOwnership = useToggleOnePieceCardOwnership();
  const toggleOpWishlist = useToggleOnePieceCardWishlist();
  const toggleOpFavorite = useToggleOnePieceCardFavorite();

  const getSetName = () => {
    if (overrideSetName) return overrideSetName;
    return card.setName || card.set_name || card.set?.name;
  };

  const isChase = game === 'onepiece' && (card as OnePieceCard).isChaseCard;
  
  // Image URL logic
  const imageUrl = game === 'pokemon' 
    ? (card.images?.small || card.thumbnailUrl) 
    : (card.images?.small || card.images?.large || '');

  const setName = getSetName();
  const shouldShowSetName = showSetName || layout === 'flat';

  // Ownership state
  const isOwned = !!ownership?.owned;
  const isFavorite = !!ownership?.isFavorite;
  const isWishlist = !!ownership?.isWishlist;

  const handleToggleOwnership = (e: React.MouseEvent) => {
    handleAction(e, () => {
      if (game === 'pokemon') {
        togglePkOwnership.mutate(card.id);
      } else {
        toggleOpOwnership.mutate({ 
          cardId: card.id, 
          setId: card.setId, 
          justTcgId: card.justtcgId 
        });
      }
    });
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    handleAction(e, () => {
      if (game === 'pokemon') {
        togglePkWishlist.mutate(card.id);
      } else {
        toggleOpWishlist.mutate({ cardId: card.id, setId: card.setId });
      }
    });
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    handleAction(e, () => {
      if (game === 'pokemon') {
        togglePkFavorite.mutate(card.id);
      } else {
        toggleOpFavorite.mutate({ cardId: card.id, setId: card.setId });
      }
    });
  };

  return (
    <Card 
      onClick={() => onClick?.(card)}
      sx={{ 
        cursor: 'pointer', 
        height: '100%', 
        position: 'relative',
        transition: 'transform 0.2s',
        border: isChase 
          ? `2px solid ${theme.palette.warning.main}` 
          : isOwned 
            ? `2px solid ${theme.palette.success.main}`
            : 'none',
        boxShadow: isChase 
          ? `0 0 10px ${alpha(theme.palette.warning.main, 0.5)}` 
          : isOwned
            ? `0 0 8px ${alpha(theme.palette.success.main, 0.3)}`
            : undefined,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        }
      }}
    >
      <Box sx={{ p: 1.5, textAlign: 'left', position: 'relative' }}>
        {/* Ownership Toggle (Top Left) */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
          <IconButton
            size="small"
            onClick={handleToggleOwnership}
            sx={{
              bgcolor: isOwned ? 'success.main' : 'rgba(255,255,255,0.8)',
              color: isOwned ? 'white' : 'text.secondary',
              '&:hover': { bgcolor: isOwned ? 'success.dark' : 'white' },
              boxShadow: 2,
              width: 28,
              height: 28
            }}
          >
            <Badge badgeContent={ownership?.quantity > 1 ? ownership.quantity : null} color="primary">
              <CollectionsIcon sx={{ fontSize: 16 }} />
            </Badge>
          </IconButton>
        </Box>

        {isChase && (
            <Chip
            icon={<StarIcon />}
            label="Chase"
            size="small"
            color="warning"
            sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                opacity: 0.9
            }}
            />
        )}

        {game === 'onepiece' && ((card as OnePieceCard).category === 'Leader' || (card as OnePieceCard).rarity === 'L') && (
            <Chip
            label="Leader"
            size="small"
            color="primary"
            sx={{
                position: 'absolute',
                top: 8,
                right: isChase ? 64 : 8,
                zIndex: 1,
                opacity: 0.9,
                fontWeight: 'bold'
            }}
            />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, position: 'relative' }}>
          <LazyImage 
            src={imageUrl} 
            alt={card.name} 
            style={{ 
              width: '100%', 
              height: 'auto', 
              aspectRatio: '2.5/3.5', 
              objectFit: 'contain',
              filter: isOwned ? 'none' : 'grayscale(30%) opacity(0.8)'
            }} 
          />

          {/* Wishlist Toggle (Bottom Left) */}
          <IconButton
            size="small"
            onClick={handleToggleWishlist}
            sx={{
              position: 'absolute',
              bottom: 4,
              left: 0,
              zIndex: 2,
              backgroundColor: isWishlist 
                ? theme.palette.info.main 
                : 'rgba(255, 255, 255, 0.7)',
              color: isWishlist ? '#fff' : 'text.secondary',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: isWishlist 
                  ? theme.palette.info.dark 
                  : 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            {isWishlist ? <ShoppingCartIcon sx={{ fontSize: 16 }} /> : <ShoppingCartOutlinedIcon sx={{ fontSize: 16 }} />}
          </IconButton>

          {/* Favorite Toggle (Bottom Right) */}
          <IconButton
            size="small"
            onClick={handleToggleFavorite}
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 0,
              zIndex: 2,
              backgroundColor: isFavorite 
                ? theme.palette.error.main 
                : 'rgba(255, 255, 255, 0.7)',
              color: isFavorite ? '#fff' : 'text.secondary',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: isFavorite 
                  ? theme.palette.error.dark 
                  : 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            {isFavorite ? <FavoriteIcon sx={{ fontSize: 16 }} /> : <FavoriteBorderIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
        
        <Typography variant="body2" noWrap fontWeight="bold">{card.name}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">#{card.number}</Typography>
        
        {shouldShowSetName && (
             <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {setName}
            </Typography>
        )}

        {/* One Piece Color Chips */}
        {game === 'onepiece' && (card as OnePieceCard).color && (
            <Stack direction="row" justifyContent="flex-start" gap={0.5} mt={0.5}>
                {(card as OnePieceCard).color.map(c => (
                    <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getOpColor(c) }} />
                ))}
            </Stack>
        )}
      </Box>
    </Card>
  );
};

export default GameCard;
